import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { ApplicationRef, DoBootstrap, ErrorHandler, NgModule } from '@angular/core';
import { MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CwpNgFrameworkModule } from '@blk/cwp-ng-framework';
import { DzFontFaceModule } from '@blk/daisy/core/font-face';
import { AnalyticsModule } from '@ddt-web/analytics';
import { GET_FONT_PATH, ReportGenerationService } from '@ddt-web/common';
import { APP_STORE_CONFIG_TOKEN, APPLICATION_NAME_TOKEN, CUSTOM_CONFIG_VARIANTS_TOKEN, IS_PUBLIC_APPLICATION, MeConfigVariants, MODELS_STORE_NAME_TOKEN } from '@ddt-web/config';
import { APPLICATION_CONFIG_PATH_TOKEN, ConfigLayerModule } from '@ddt-web/config-layer';
import { AppBootstrapModule, AppBootstrapService, CoreHttpModule, ModelsInterceptorService, PartnerInterceptorService } from '@ddt-web/core';
import { CoreModule } from '@ddt-web/data-layer-components';
import { DataServiceModule } from '@ddt-web/data-service';
import { DialogModule } from '@ddt-web/dialog';
import { DomModule } from '@ddt-web/dom';
import { ApplicationNames, WebUserDataConstants } from '@ddt-web/domain';
import { CommonErrorHandlerService, ErrorHandlerModule, ErrorHandlerService, OktaErrorHandlerService, PasModelAnalyticsErrorHandlerService, PasModelFamiliesErrorHandlerService, SecurityDetailsProcessingErrorHandler } from '@ddt-web/error-handler';
import { FeatureFlagModule } from '@ddt-web/feature-flag';
import { HighlightedFiltersModule, ModelsBrowseReportConfigService, ModelsBrowseStoreConfig, ModelsBrowsingModule, PersistentNavigationModule } from '@ddt-web/models-browsing';
import { PasModule, PreviewDataBannerModule } from '@ddt-web/pas';
import { ReportConfigService } from '@ddt-web/pdf-generate';
import { SetupDataWorkersModule } from '@ddt-web/setup-data-workers';
import { ApplicationStateStoreProxyService } from '@ddt-web/shared-stores';

import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppStoreModule } from './app-store.module';
import { ModelsExperienceComponent } from './models-experience.component';

const BOOTSTRAP_COMPONENTS = { 'me-models-experience-root': ModelsExperienceComponent };

@NgModule({
	declarations: [ModelsExperienceComponent],
	imports: [
		AnalyticsModule,
		BrowserModule,
		BrowserAnimationsModule,
		AppBootstrapModule.forRoot(environment, BOOTSTRAP_COMPONENTS, { isLazyLoad: true, isHtml5Strategy: true }),
		AppStoreModule.forRoot(),
		AppRoutingModule,
		ConfigLayerModule,
		CoreHttpModule.forRoot({
			interceptors: [{
				provide: HTTP_INTERCEPTORS,
				useClass: PartnerInterceptorService,
				multi: true
			}, {
				provide: HTTP_INTERCEPTORS,
				useClass: ModelsInterceptorService,
				multi: true
			}]
		}),
		CoreModule.forRoot(),
		CwpNgFrameworkModule.forRoot('me-models-experience-root'),
		DataServiceModule.forRoot(),
		ErrorHandlerModule.forRoot({
			useErrorHandlers: [
				OktaErrorHandlerService,
				SecurityDetailsProcessingErrorHandler,
				PasModelFamiliesErrorHandlerService,
				PasModelAnalyticsErrorHandlerService,
				CommonErrorHandlerService
			]
		}),
		SetupDataWorkersModule.forRoot(),
		MatDialogModule,
		DialogModule,
		PersistentNavigationModule,
		HighlightedFiltersModule,
		ModelsBrowsingModule,
		FeatureFlagModule.forRoot({ applicationStateStoreProxyService: ApplicationStateStoreProxyService }),
		DomModule,
		DzFontFaceModule.addFonts(GET_FONT_PATH(environment)),
		PasModule,
		PreviewDataBannerModule
	],
	providers: [
		{
			provide: APPLICATION_NAME_TOKEN,
			useValue: ApplicationNames.MODELS_EXPERIENCE
		},
		{
			provide: APPLICATION_CONFIG_PATH_TOKEN,
			useValue: WebUserDataConstants.APPLICATIONS.BFA
		},
		{
			provide: ErrorHandler,
			useClass: ErrorHandlerService
		},
		{
			provide: IS_PUBLIC_APPLICATION,
			useValue: true
		},
		{
			provide: APP_STORE_CONFIG_TOKEN,
			useValue: ModelsBrowseStoreConfig
		},
		{
			provide: MODELS_STORE_NAME_TOKEN,
			useValue: 'modelsConfig'
		},
		{
			provide: ReportConfigService,
			useClass: ModelsBrowseReportConfigService
		},
		{
			provide: CUSTOM_CONFIG_VARIANTS_TOKEN,
			useValue: [
				MeConfigVariants.PERFORMANCE_AS_OF_DATE
			]
		},
		ReportGenerationService
	]
})
export class AppModule implements DoBootstrap {
	constructor(
		private appBootstrapService: AppBootstrapService
	) { }

	public ngDoBootstrap(appRef: ApplicationRef): void {
		this.appBootstrapService.bootstrap(appRef);
	}
}
